import { useState } from 'react'

import { useLocation } from 'react-router-dom'
import { styled } from 'styled-components'

import { redirectToApp } from 'app/loginRedirect/loginRedirectHelpers'

import {
  getSessionExpired,
  getStoredToken,
  decodeToken,
  setSessionExpired,
  setStoredToken,
  isTokenExpired,
  isTokenValid,
  setSessionExpiredIfRecent,
  getQueryUTM,
  removeUTMQueryParams,
  PEACH_UTM,
} from 'core/actions/helpers/tokenHelpers'
import { Alert, SetPageTitle, Spinner } from 'core/components'
import { assert } from 'core/helpers'
import { storeSession, useOnMount } from 'core/hooks'
import { useAppDispatch } from 'core/hooks/useAppStore'
import { isAuthType, useBootstrapStore } from 'core/stores'
import { AuthType, useValidateAndSendToken } from 'core/types'

import BasicPasswordLogin from './Basic/BasicPasswordLogin'
import CodeLogin from './CodeLogin'
import { loginWithPassword } from './loginApi'
import LoginErrorMessage from './LoginErrorMessage'
import SamlLogin from './SAML/SamlLogin'
import useSamlParams from './SAML/useSamlParams'

const AlertWrapper = styled.div`
  margin: 0 auto 18px;
  max-width: 560px;
`

const storeAuthData = (token: string, username?: string) => {
  const { personId, userType } = decodeToken(token)
  setStoredToken(token)
  localStorage.setItem('personIdFromToken', JSON.stringify(personId))
  localStorage.setItem('userTypeFromToken', userType)
  if (username) {
    localStorage.setItem('username', username)
  } else {
    localStorage.removeItem('username')
  }
}

const LoginManager = () => {
  const { hasSamlCreds, immediatelyRedirect } = useSamlParams()
  const { userType, company, authType } = useBootstrapStore() ?? {}
  const companyId = company?.id

  assert(userType && companyId && authType, 'Bootstrap values are unset')

  const utm = getQueryUTM()

  const { search } = useLocation()
  const exchangeToken = new URLSearchParams(search).get('token')

  const [showSessionExpired, setShowSessionExpired] = useState(false)

  const {
    mutateAsync: sendToken,
    isPending: isExchangeTokenLoading,
    error: exchangeTokenError,
  } = useValidateAndSendToken({
    query: { companyId, userType },
    options: {
      onError: undefined,
    },
  })

  const dispatch = useAppDispatch()

  const onSuccess = (token: string, username?: string) => {
    storeAuthData(token, username)
    dispatch({ type: 'GENERATE_TOKEN_SUCCESS' })
    storeSession(PEACH_UTM, utm)
    redirectToApp()
    removeUTMQueryParams()
  }

  const onLogin = async ({
    username,
    password,
    selectedAuthType = authType,
  }: {
    username: string
    password: string
    selectedAuthType?: AuthType
  }) => {
    const resp = await loginWithPassword({
      username,
      password,
      companyId,
      userType,
      authType: selectedAuthType,
    })
    onSuccess(resp.token, username)
    return resp
  }

  const token = getStoredToken()
  const isValidAuthType = isAuthType(authType)

  useOnMount(async () => {
    // Handle SAML login in <SamlLogin />
    if (authType === 'SAML' && (hasSamlCreds || immediatelyRedirect)) return

    // Token. Remove token from localStorage if expired
    if (isValidAuthType) {
      if (token && isTokenExpired(token)) {
        setSessionExpiredIfRecent()
        setStoredToken('')
      } else if (isTokenValid(token)) {
        onSuccess(token)
      }
    }
    // show session expiry message only once and remove flag from storage
    if (getSessionExpired()) {
      setSessionExpired(false)
      setShowSessionExpired(true)
    }

    if (exchangeToken) {
      try {
        const resp = await sendToken({ exchangeToken })
        onSuccess(resp.token)
      } catch {
        // Error is handled using `exchangeTokenError`
      }
    }
  })

  return (
    <>
      <SetPageTitle userType={userType} />
      {exchangeToken ?
        <LoginErrorMessage>
          {exchangeTokenError ?
            (exchangeTokenError as any)?.message
          : isExchangeTokenLoading ?
            <Spinner />
          : undefined}
        </LoginErrorMessage>
      : isValidAuthType ?
        <>
          {showSessionExpired && (
            <AlertWrapper>
              <Alert
                description='Your session timed out. Please log in again to access your account.'
                icon='hourglass_empty'
                title='Session Timed Out'
                variant='info'
              />
            </AlertWrapper>
          )}
          {authType === 'basic' && <BasicPasswordLogin onLogin={onLogin} />}
          {(authType === 'oneTimeCodeText' || authType === 'oneTimeCodeEmail') && <CodeLogin onLogin={onLogin} />}
          {authType === 'SAML' && <SamlLogin onLogin={onLogin} />}
        </>
      : <LoginErrorMessage>Unsupported Auth Type</LoginErrorMessage>}
    </>
  )
}

export default LoginManager
